/**
 * Main application entry point
 * Kiosk Weather Display Application
 */

import './bootstrap.js';
import './styles/app.css';

// Import configuration and utilities
import { MILLISECONDS_PER_SECOND, REFRESH_INTERVALS } from './config.js';
import { updateTimeElements } from './utils/time.js';
import { setContentHeight, initSensorNameFitting } from './utils/dom.js';
import { setWindObs, getCheckTimes, getLastCheck, getSensorHealth } from './modules/state.js';

// Import modules
import { initializeApp } from './modules/init.js';
import { setupChoreHandlers, updateChore, populateChoreResetModal } from './modules/chores.js';
import { startTemperatureRotation } from './modules/weather.js';
import { initializeCharts } from './modules/charts.js';
import { updateForecast, updateRadar, updateWarnings, updateSensors } from './modules/updates.js';
import { loadWeatherTimeseries, loadSensorTimeseries } from './modules/timeseries.js';
import { initializeTheme } from './modules/theme.js';
import { startPixelShift } from './modules/pixel-shift.js';
import { initializeRecords } from './modules/records.js';
import { initializeWindRose } from './modules/windrose.js';
import { initializeCwg } from './modules/cwg.js';
import { initAutoRotate } from './modules/autorotate.js';
import { updateAppliances, showApplianceDetail } from './modules/appliances.js';
import { updateVacuums, sendVacuumCommand } from './modules/vacuums.js';

// Event Handlers
function setupEventHandlers() {
  // Make sure jQuery is available
  if (typeof jQuery === 'undefined') {
    console.error('[App] jQuery is not loaded!');
    return;
  }

  // Chart click handler
  jQuery('[id^="chart-"]').on('click', function () {
    const chartId = this.id.split('-');
    chartId.shift();
    const chartType = chartId.join('-');
    loadWeatherTimeseries(chartType);
  });

  // Appliance click handler. Delegated, because the cards are re-rendered on
  // every poll; Foundation's data-open trigger opens the modal itself.
  jQuery(document).on('click', '.appliance-holder', function () {
    showApplianceDetail(jQuery(this).data('device-id'));
  });

  // Vacuum buttons. Delegated for the same reason as the appliance cards.
  jQuery(document).on('click', '.vacuum-btn', function () {
    sendVacuumCommand(jQuery(this).data('vacuum-id'), jQuery(this).data('action'));
  });

  // Sensor click handler
  jQuery('.sensor-holder').on('click', function () {
    const sensorInfo = {
      sku: jQuery(this).data('sensor-sku'),
      device: jQuery(this).data('sensor-device'),
      deviceId: jQuery(this).data('sensor-id'),
    };
    loadSensorTimeseries(sensorInfo);
  });

  // Current weather tab handler
  jQuery('#current-weather').on('click', () => {
    jQuery('#content-tabs').foundation('selectTab', 'content-weather');
  });

  // Forecast day handler
  jQuery(document).on('click', '.forecast-day', function () {
    const clicked = `#forecast-hourly-day-${  jQuery(this).data('forecast-day')}`;

    if (!jQuery(clicked).is(':visible')) {
      jQuery('.forecast-hourly').hide();
      jQuery(clicked).css('display', 'flex');
      jQuery(document).foundation();
    } else {
      jQuery('.forecast-hourly').hide();
    }
  });

  // Weather alert handler
  jQuery(document).on('click', '.weather-alert', function () {
    const warnings = window.warnings || {};
    const alertId = jQuery(this).data('alertid');
    if (warnings[alertId]) {
      jQuery('#modal-warning-event').text(warnings[alertId]['event']);
      jQuery('#modal-warning-nws').text(warnings[alertId]['nwsheadline']);
      let wtxt = warnings[alertId]['description'].replace(/(?:\r\n|\r|\n)/g, '<br>');
      jQuery('#modal-warning-description').html(wtxt);
      wtxt = warnings[alertId]['instruction'].replace(/(?:\r\n|\r|\n)/g, '<br>');
      jQuery('#modal-warning-instruction').html(wtxt);
    }
  });

  // Helper function to format elapsed time in human-friendly format
  function formatElapsedTime(lastCheckTime) {
    if (!lastCheckTime) {
      return 'Never';
    }

    const now = new Date();
    const elapsed = now - lastCheckTime;
    const seconds = Math.floor(elapsed / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) {
      return seconds === 1 ? '1 second ago' : `${seconds} seconds ago`;
    } else if (minutes < 60) {
      return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
    } else if (hours < 24) {
      return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
    } else {
      return days === 1 ? '1 day ago' : `${days} days ago`;
    }
  }

  // Status label handler - Enhanced to show all service statuses
  jQuery(document).on('click', '.status-label', () => {
    // Collect every row, then split across two columns so the modal stays short.
    const rows = [];

    const now = new Date();
    const lastCheck = getLastCheck();
    const checkTimes = getCheckTimes();

    // Define services to display in the status modal
    const services = [
      { id: 'wsStatusTempest', name: 'Tempest Connection', key: 'Weather', updateId: 'wsUpdateTempest' },
      { id: 'wsStatusWeather', name: 'Weather Stream', key: 'Weather', updateId: 'wsUpdateWeather' },
      { id: 'wsStatusForecast', name: 'Forecast Data', key: 'Forecast', updateId: 'wsUpdateForecast' },
      { id: 'wsStatusRadar', name: 'Radar Images', key: 'Radar', updateId: 'wsUpdateRadar' },
      { id: 'wsStatusWarnings', name: 'Weather Warnings', key: 'Warnings', updateId: 'wsUpdateWarnings' },
      { id: 'wsStatusSensors', name: 'Sensor Data', key: 'Sensors', updateId: 'wsUpdateSensors' },
      { id: 'wsStatusChore', name: 'Chore System', key: 'Chore', updateId: 'wsUpdateChore' },
      { id: 'wsStatusAppliances', name: 'LG Appliances', key: 'Appliances', updateId: 'wsUpdateAppliances' },
    ];

    services.forEach(service => {
      const $element = jQuery(`#${  service.id}`);
      let statusText = 'Unknown';
      let statusClass = 'secondary';

      // Determine status from element classes
      if ($element.hasClass('success')) {
        statusText = 'Operational';
        statusClass = 'success';
      } else if ($element.hasClass('alert')) {
        statusText = 'Error';
        statusClass = 'alert';
      } else if ($element.hasClass('secondary')) {
        statusText = 'Loading...';
        statusClass = 'secondary';
      }

      // Check if data is stale based on last check time
      const lastCheckTime = lastCheck[service.key];
      const checkInterval = checkTimes[service.key];
      if (lastCheckTime && checkInterval && (now - lastCheckTime > (2 * checkInterval))) {
        statusText = 'Stale Data';
        statusClass = 'alert';
      }

      // Create status indicator
      const statusIndicator = jQuery('<span>')
        .addClass('label')
        .addClass(statusClass)
        .text(statusText);

      // Get last update time and format with elapsed time
      const updateText = jQuery(`#${  service.updateId}`).text();
      const elapsedTime = formatElapsedTime(lastCheckTime);

      // Combine absolute time and relative time
      let timeDisplay;
      if (updateText && updateText !== '' && updateText !== 'Never') {
        // If we have absolute time (like "3:45:12 PM"), show both
        timeDisplay = jQuery('<div>')
          .append(jQuery('<div>').text(updateText).css('font-weight', 'bold'))
          .append(jQuery('<div>').text(elapsedTime).css({ 'font-size': '0.85em', 'color': '#888' }));
      } else {
        // Otherwise just show elapsed time
        timeDisplay = elapsedTime;
      }

      const srow = jQuery('<tr>').addClass(`table-${  statusClass}`);
      srow.append(jQuery('<td>').text(service.name));
      srow.append(jQuery('<td>').html(statusIndicator));
      srow.append(jQuery('<td>').html(timeDisplay));

      rows.push(srow);
    });

    // Sensor health summary (offline / low battery), from the latest poll
    const sensorHealth = getSensorHealth();
    if (sensorHealth) {
      const healthRow = (label, value, isAlert) => {
        const row = jQuery('<tr>').addClass(`table-${isAlert ? 'alert' : 'success'}`);
        row.append(jQuery('<td>').text(label));
        row.append(jQuery('<td>').attr('colspan', 2).html(
          jQuery('<span>').addClass(`label ${isAlert ? 'alert' : 'success'}`).text(value),
        ));
        return row;
      };

      rows.push(
        jQuery('<tr>').append(
          jQuery('<td>').attr('colspan', 3).css({ 'font-weight': 'bold', 'padding-top': '0.75rem' }).text('Sensor Health'),
        ),
      );
      rows.push(healthRow(
        'Reporting',
        `${sensorHealth.total - sensorHealth.offline} / ${sensorHealth.total}`,
        sensorHealth.offline > 0,
      ));
      rows.push(healthRow('Offline', String(sensorHealth.offline), sensorHealth.offline > 0));
      rows.push(healthRow('Low battery', String(sensorHealth.lowBattery), sensorHealth.lowBattery > 0));
    }

    // Build a status table (with header) from a subset of rows
    const buildColumn = (columnRows) => {
      const table = jQuery('<table>').addClass('status-table-table');
      const header = jQuery('<tr>');
      header.append(jQuery('<th>').text('Service'));
      header.append(jQuery('<th>').text('Status'));
      header.append(jQuery('<th>').text('Last Update'));
      table.append(header);
      columnRows.forEach(r => table.append(r));
      return jQuery('<div>').append(table);
    };

    // Split the rows into two balanced columns
    const mid = Math.ceil(rows.length / 2);
    const statusColumns = jQuery('<div>').addClass('status-columns');
    statusColumns.append(buildColumn(rows.slice(0, mid)));
    statusColumns.append(buildColumn(rows.slice(mid)));

    // Add buttons at the bottom
    const buttonContainer = jQuery('<div>')
      .css({ 'text-align': 'center', 'margin-top': '1.5rem', 'display': 'flex', 'justify-content': 'center', 'gap': '1rem', 'flex-wrap': 'wrap' });

    const resetButton = jQuery('<button>')
      .addClass('button alert')
      .text('Reset Completed Chores')
      .attr('id', 'reset-chores-from-status');

    // Determine current theme for button label
    const isDark = document.documentElement.hasAttribute('data-theme');
    const themeButton = jQuery('<button>')
      .addClass('button secondary')
      .attr('id', 'theme-toggle')
      .attr('title', 'Toggle theme (10-minute manual override)')
      .html(`<span id="theme-icon" style="margin-right: 0.5rem;">${isDark ? '☀️' : '🌙'}</span><span id="theme-label">${isDark ? 'Light Mode' : 'Dark Mode'}</span>`);

    buttonContainer.append(themeButton).append(resetButton);

    jQuery('#status-table').html(statusColumns).append(buttonContainer);
    updateTimeElements();
  });

  // Handle chore reset button click from status modal
  jQuery(document).on('click', '#reset-chores-from-status', () => {
    // Close status modal
    jQuery('#modal-status-table').foundation('close');

    // Open chore reset modal
    setTimeout(() => {
      jQuery('#modal-chore-reset').foundation('open');
    }, 300);
  });

  // Chore reset modal - populate when opened
  jQuery('#modal-chore-reset').on('open.zf.reveal', () => {
    populateChoreResetModal();
  });
}

// Application startup - Wait for both DOM and jQuery to be ready
function startApp() {
  // Verify jQuery is loaded
  if (typeof jQuery === 'undefined') {
    console.error('[App] jQuery not loaded, waiting...');
    setTimeout(startApp, 100);
    return;
  }

  // Verify Foundation is loaded
  if (typeof jQuery.fn.foundation === 'undefined') {
    console.error('[App] Foundation not loaded, waiting...');
    setTimeout(startApp, 100);
    return;
  }

  // Check if this is the CWG page - initialize CWG module instead
  if (window.CWG_PAGE) {
    jQuery(document).foundation();
    initializeCwg();
    return;
  }

  // Initialize theme (must be first to avoid flash of wrong theme)
  initializeTheme();

  // Initialize Foundation
  jQuery(document).foundation();

  // Set up clock
  if (typeof W3FCT !== 'undefined') {
    W3FCT.lang.mySpanishLang = {
      days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      ampm: ['am', 'pm'],
    };
    const clockElement = document.getElementById('clock');
    if (clockElement && clockElement.W3FClockTimer) {
      clockElement.W3FClockTimer({ lang: 'mySpanishLang' });
    }
  }

  // Initialize time element updates
  updateTimeElements();
  setInterval(updateTimeElements, 60 * MILLISECONDS_PER_SECOND);

  // Set up event handlers
  setupEventHandlers();
  setupChoreHandlers();

  // Auto-rotate between the Weather and Sensors tabs (pauses on interaction)
  initAutoRotate();

  // Start temperature rotation
  startTemperatureRotation();

  // Initialize charts (using data from template)
  if (typeof Highcharts !== 'undefined' && typeof cWind !== 'undefined') {
    try {
      initializeCharts({
        cWind: window.cWind,
        cPrecip: window.cPrecip,
        cUV: window.cUV,
        cDP: window.cDP,
        cT: window.cT,
        cPM: window.cPM,
      });
      // Initialize wind observations with the actual cWind value
      setWindObs([window.cWind]);
    } catch (error) {
      console.error('[App] Failed to initialize charts:', error);
    }
  } else {
    console.warn('[App] Charts not initialized - missing Highcharts or chart data');
  }

  // Initialize application (load config, start WebSocket)
  initializeApp().then((initialized) => {
    if (!initialized) {
      console.error('[App] Failed to initialize application');
      return;
    }

    // Update sensor status
    jQuery('#wsUpdateSensors').text(new Date().toLocaleTimeString());

    // Start periodic updates for chores
    updateChore();
    setInterval(updateChore, REFRESH_INTERVALS.CHORE);

    // Start periodic updates for weather data
    updateForecast();
    setInterval(updateForecast, REFRESH_INTERVALS.FORECAST);

    updateRadar();
    setInterval(updateRadar, REFRESH_INTERVALS.RADAR);

    updateWarnings();
    setInterval(updateWarnings, REFRESH_INTERVALS.WARNINGS);

    updateSensors();
    setInterval(updateSensors, REFRESH_INTERVALS.SENSORS);

    updateAppliances();
    setInterval(updateAppliances, REFRESH_INTERVALS.APPLIANCES);

    updateVacuums();
    setInterval(updateVacuums, REFRESH_INTERVALS.VACUUMS);

    // Initialize records (load once on startup)
    initializeRecords();

    // Initialize wind rose (24hr distribution)
    initializeWindRose();

    // Start pixel shifting for burn-in prevention
    startPixelShift();

    // Fit sensor names to their containers
    initSensorNameFitting();
  });
}

// Wait for DOM to be ready, then start app
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startApp);
} else {
  // DOM is already ready
  startApp();
}

// Export for debugging
if (typeof window !== 'undefined') {
  window.KioskApp = {
    updateChore,
    populateChoreResetModal,
    updateTimeElements,
    setContentHeight,
  };
}
